import { useMemo } from 'react';

import compact from 'lodash/compact';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import qs from 'query-string';
import { useLocation } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';

import Authenticated from 'src/app/components/Auth/Authenticated';
import { login, medications, notFound, clearRxNotFound } from 'src/app/router/paths';
import routes, { layouts } from 'src/app/router/routes';
import type { RedirectedRoute, Route as TypicalRoute } from 'src/app/router/routes';

import { Provider } from './context';

/**
 * Renders the layout used by the current route
 */
export const LayoutManager = () => {
  const redirectRoutes = useMemo(() => routes.filter(route => 'redirect' in route) as RedirectedRoute[], []);
  const isClearRxPage = typeof window !== undefined ? window.location.host.includes('clearrx.co') : false;
  const authenticatedRoutes = useMemo(
    () => routes.filter(route => 'isAuthenticated' in route && route.isAuthenticated) as TypicalRoute[],
    []
  );

  const unauthenticatedRoutes = useMemo(
    () => routes.filter(route => !('isAuthenticated' in route) || !!!route.isAuthenticated) as TypicalRoute[],
    []
  );

  // Groups routes by the layout they use so that we can support transitions within a layout
  const groupPathsByLayout = (routes: TypicalRoute[]) =>
    reduce(
      groupBy(routes, 'layout.displayName'),
      (result, value, key) => {
        result[key] = compact(map(value, 'path'));
        return result;
      },
      {} as ObjectWithUnknownKeys
    );

  const groupedRoutes = useMemo(() => groupPathsByLayout(unauthenticatedRoutes), [unauthenticatedRoutes]);

  const groupedAuthenticatedRoutes = useMemo(() => groupPathsByLayout(authenticatedRoutes), [authenticatedRoutes]);

  const location = useLocation();
  const { code, capsuleCode, userUuid } = qs.parse(location.search);
  const isCodeDetected = userUuid && code && (code as string).split('.').length === 2;
  const isCapsuleCodeDetected = userUuid && capsuleCode && (capsuleCode as string).split('.').length === 2;

  if ((isCapsuleCodeDetected || isCodeDetected) && location.pathname === medications.toPath() && !isClearRxPage) {
    return <Redirect to={{ ...location, pathname: login.route }} />;
  }

  return (
    <Provider value={routes as any}>
      <Switch>
        {map(groupedRoutes, (group, key) => {
          return (
            <Route path={group} exact={true} component={layouts[key as keyof typeof layouts]} key={`layout-${key}`} />
          );
        })}

        {map(groupedAuthenticatedRoutes, (group, key) => {
          return (
            <Route path={group} exact={true} key={`layout-${key}`}>
              <Authenticated>
                <Route
                  path={group}
                  exact={true}
                  component={layouts[key as keyof typeof layouts]}
                  key={`layout-i-${key}`}
                />
              </Authenticated>
            </Route>
          );
        })}

        {map(redirectRoutes, (route, index) => {
          const redirectRoute = `${route.to}${route.queryParams ? route.queryParams : ''}`;

          return <Redirect from={route.from} to={redirectRoute} key={`redirect-${index}-${route.from}`} />;
        })}

        <Route
          render={({ location }) => {
            return (
              <Redirect
                to={{ pathname: isClearRxPage ? clearRxNotFound.route : notFound.route, state: { referrer: location } }}
              />
            );
          }}
        />
      </Switch>
    </Provider>
  );
};
